import axios from './axiosConfig';

const PING_LOG_API_BASE = '/api/pinglogs';

export interface ApiResponse<T> {
    code: number;
    message: string;
    data: T;
}

export interface PingLogListItem {
    id?: number;
    logId: string;
    tenantId?: number;
    userId?: number;
    userName?: string;
    fileName: string;
    logType: string;
    agentId: string;
    deviceName: string;
    targetIp: string;
    sourceIp: string;
    totalRounds: number;
    parsedRounds: number;
    startTimeUtc?: string;
    endTimeUtc?: string;
    startClockLine?: string;
    endClockLine?: string;
    avgLossPercent: number;
    avgRttMs?: number | null;
    maxRttMs?: number | null;
    status?: string;
    errorMessage?: string;
    uploadTime?: string;
    createdAt?: string;
}

export interface PingLogListResponseData {
    items: PingLogListItem[];
    total: number;
    page: number;
    pageSize: number;
}

export interface PingLogRecord {
    id?: number;
    logId?: string;
    tenantId?: number;
    deviceName: string;
    vrf: string;
    targetIp: string;
    sourceIp: string;
    repeatCount: number;
    packetSize: number;
    df: number | boolean;
    resultPattern: string;
    successRate: number;
    sent: number;
    received: number;
    lossPercent: number;
    rttMinMs?: number | null;
    rttAvgMs?: number | null;
    rttMaxMs?: number | null;
    timestampUtc?: string;
    rawClockLine: string;
    createdAt?: string;
}

export interface PingLogTask {
    id?: number;
    logId: string;
    tenantId?: number;
    fileName: string;
    savedPath?: string;
    fileSize?: number;
    fileHash?: string;
    logType: string;
    agentId: string;
    deviceName: string;
    targetIp: string;
    sourceIp: string;
    totalRounds: number;
    parsedRounds: number;
    startTimeUtc?: string;
    endTimeUtc?: string;
    startClockLine?: string;
    endClockLine?: string;
    avgLossPercent: number;
    avgRttMs?: number | null;
    maxRttMs?: number | null;
    status?: string;
    errorMessage?: string;
    uploadTime?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface PingLogDetailData {
    task: PingLogTask;
    userId?: number;
    userName?: string;
    records: PingLogRecord[];
}

export interface GetPingLogsParams {
    page?: number;
    pageSize?: number;
    logId?: string;
    deviceName?: string;
    targetIp?: string;
    sourceIp?: string;
    agentId?: string;
}

export interface UploadSecureCRTPingLogResponse {
    logId?: string;
    fileName?: string;
    savedPath?: string;
    logType?: string;
    agentId?: string;
    deviceName?: string;
    targetIp?: string;
    sourceIp?: string;
    totalRounds?: number;
    parsedRounds?: number;
    startTimeUtc?: string;
    endTimeUtc?: string;
    startClockLine?: string;
    endClockLine?: string;
    avgLossPercent?: number;
    avgRttMs?: number | null;
    maxRttMs?: number | null;
    status?: string;
    uploadTime?: string;
    duplicated?: boolean;
}

export interface DeletePingLogResponse {
    logId?: string;
}

export async function getPingLogs(params?: GetPingLogsParams): Promise<PingLogListResponseData> {
    const res = await axios.get<ApiResponse<PingLogListResponseData>>(PING_LOG_API_BASE, {
        params: {
            page: params?.page,
            pageSize: params?.pageSize,
            logId: params?.logId,
            deviceName: params?.deviceName,
            targetIp: params?.targetIp,
            sourceIp: params?.sourceIp,
            agentId: params?.agentId,
        },
    });
    return res.data.data;
}

export async function getPingLogDetail(logId: string): Promise<PingLogDetailData> {
    const res = await axios.get<ApiResponse<PingLogDetailData>>(
        `${PING_LOG_API_BASE}/${logId}`
    );
    return res.data.data;
}

export async function uploadSecureCRTPingLog(file: File): Promise<UploadSecureCRTPingLogResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await axios.post<ApiResponse<UploadSecureCRTPingLogResponse>>(
        `${PING_LOG_API_BASE}/upload/securecrt`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    return res.data.data;
}

export async function deletePingLog(logId: string): Promise<DeletePingLogResponse> {
    const res = await axios.delete<ApiResponse<DeletePingLogResponse>>(
        `${PING_LOG_API_BASE}/${logId}`
    );
    return res.data.data;
}